import { getApiService } from "@/services/apiService";
import { ApiError, LoginResponse, RefreshTokenResponse } from "@/types/auth";
import React, { useCallback, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../../zustand/Zustand";
import { Sentry } from '@/helpers/sentry';

const AuthHandler: React.FC = () => {
  const {
    tokens,
    setIsAuthenticated,
    setUserData,
    clearAuth,
    updateAccessToken,
    setTokens
  } = useAuth();
  const api = getApiService();
  const abortControllerRef = useRef<AbortController | null>(null);
  const isAuthenticatingRef = useRef<boolean>(false);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const refreshAccessTokenRef = useRef<() => Promise<boolean>>(null);

  const handleAuthFailure = useCallback((errorMessage: string, shouldClearAuth = true) => {
    Sentry.logger.warn("Authentication failed", { errorMessage });

    if (!errorMessage.includes('expired') && !errorMessage.includes('refresh')) {
      toast.error(errorMessage);
    }

    if (shouldClearAuth) {
      clearAuth();
    }
  }, [clearAuth]);

  const scheduleTokenRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    refreshTimeoutRef.current = setTimeout(() => {
      refreshAccessTokenRef.current?.();
    }, 45 * 60 * 1000);
  }, []);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- complex async flow with token refresh & retry; React Compiler can't optimize but pattern is correct
  const refreshAccessToken = useCallback(async (): Promise<boolean> => {
    if (!tokens?.refreshToken) {
      return false;
    }

    try {
      const response = await api.post(`/shared/users/refresh-token`, JSON.stringify({
        refreshToken: tokens.refreshToken,
      }));

      if (response.ok) {
        const data: RefreshTokenResponse = await response.json();

        setTokens({
          accessToken: data.accessToken,
          refreshToken: tokens.refreshToken,
          tokenId: data.tokenId
        });

        scheduleTokenRefresh();
        return true;
      } else {
        const errorData: ApiError = await response.json();
        Sentry.logger.warn('❌ Token refresh failed', { error: errorData.error });

        if (errorData.code === 'REFRESH_TOKEN_EXPIRED' || response.status === 401) {
          handleAuthFailure("Session expired. Please log in again.", true);
        }

        return false;
      }
    } catch (error) {
      Sentry.logger.warn('❌ Token refresh error', { error });
      return false;
    }
  }, [tokens?.refreshToken, api, setTokens, scheduleTokenRefresh, handleAuthFailure]);

  // Keep ref in sync so scheduleTokenRefresh can call the latest version.
  // Set in an effect to avoid the "ref access during render" lint rule.
  useEffect(() => {
    refreshAccessTokenRef.current = refreshAccessToken;
  }, [refreshAccessToken]);

  const authenticateUserRef = useRef<() => Promise<void>>(undefined);

  const authenticateUser = useCallback(async (): Promise<void> => {
    if (!tokens?.accessToken || isAuthenticatingRef.current) {
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    isAuthenticatingRef.current = true;

    try {
      const response = await api.get(`/shared/users/login-with-token`,
        {
          signal: abortControllerRef.current.signal,
        }
      );

      if (abortControllerRef.current.signal.aborted) {
        return;
      }

      if (response.status === 401) {
        const errorData: ApiError = await response.json();

        if (errorData.code === 'TOKEN_EXPIRED' || errorData.error?.includes('expired')) {
          const refreshSuccess = await refreshAccessToken();

          if (refreshSuccess) {
            setTimeout(() => {
              authenticateUserRef.current?.();
            }, 100);
            return;
          } else {
            handleAuthFailure("Session expired. Please log in again.", true);
            return;
          }
        } else {
          handleAuthFailure(errorData.error || "Authentication failed", true);
          return;
        }
      }

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = "Authentication failed";

        try {
          const errorData: ApiError = JSON.parse(errorText);
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = response.statusText || errorMessage;
        }

        handleAuthFailure(errorMessage, true);
        return;
      }

      const data: LoginResponse = await response.json();

      if (data.user) {
        setIsAuthenticated(true);
        setUserData(data.user);
        scheduleTokenRefresh();

        if (data.accessToken) {
          updateAccessToken(data.accessToken);
        }
      } else {
        handleAuthFailure("Invalid response from server", true);
      }

    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }

      Sentry.logger.warn("Authentication error", { error });

      let errorMessage = "An error occurred during authentication";
      if (error instanceof TypeError && error.message.includes('fetch')) {
        errorMessage = "Network error - please check your connection";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      handleAuthFailure(errorMessage, true);
    } finally {
      isAuthenticatingRef.current = false;
    }
  }, [tokens?.accessToken, api, refreshAccessToken, handleAuthFailure, setIsAuthenticated, setUserData, scheduleTokenRefresh, updateAccessToken]);

  // Keep ref synced so the recursive retry path (after token refresh) can call the latest version.
  useEffect(() => {
    authenticateUserRef.current = authenticateUser;
  }, [authenticateUser]);

  useEffect(() => {
    if (tokens?.accessToken && tokens?.refreshToken) {
      authenticateUser();
    } else if (tokens?.refreshToken && !tokens?.accessToken) {
      refreshAccessToken();
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [tokens?.accessToken, tokens?.refreshToken, authenticateUser, refreshAccessToken]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  return null;
};

export default AuthHandler;